import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import i18n from "i18next";

const t = i18n.t.bind(i18n);

export const PricebandExcelReport = async (
  selectedBands = null,
  exportType = "grouped",
) => {
  try {
    const response = await http.get(
      `${BaseUrl}/products/exportPriceBandExcelReport`,
    );
    const data = response.data;
    if (!data || data.length === 0) {
      alert(t("no_data_available_txt"));
      return;
    }
    const allBands = [...new Set(data.map((item) => item.priceBand))];
    const isAllSelected =
      !selectedBands ||
      selectedBands.length === 0 ||
      selectedBands.length === allBands.length;
    const totalText = isAllSelected
      ? t("total_active_price_bands_txt")
      : t("total_selected_price_bands_txt");
    const totalCount = isAllSelected ? allBands.length : selectedBands.length;
    let filteredData = data;
    if (selectedBands && selectedBands.length > 0) {
      filteredData = data.filter((item) =>
        selectedBands.includes(item.priceBand),
      );
    }
    // Group by priceBand
    const grouped = {};
    filteredData.forEach((item) => {
      if (!grouped[item.priceBand]) {
        grouped[item.priceBand] = [];
      }
      grouped[item.priceBand].push(item);
    });
    const totalPriceBands = Object.keys(grouped).length;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t("price_band_report_tab_txt"));
    let rowIndex = 1;
    if (exportType === "column") {
      // Column based: single table with Price Band as first column
      worksheet.getCell("A1").value = t("price_band_report_tab_txt");
      worksheet.getCell("A1").font = { bold: true, size: 16 };
      worksheet.mergeCells("A1:E1");
      worksheet.getCell("A1").alignment = { horizontal: "center" };
      worksheet.getCell("A2").value = `${totalText} ${totalCount}`;
      worksheet.getCell("A2").font = { bold: true };
      rowIndex = 4;
      const headerRow = worksheet.getRow(rowIndex);
      headerRow.values = [
        t("price_band_txt"),
        t("product_code_txt"),
        t("description_txt"),
        t("price(£)_txt"),
        t("product_status_txt"),
      ];
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4472C4" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
      rowIndex++;
      // Sort data by priceBand
      filteredData.sort((a, b) => a.priceBand.localeCompare(b.priceBand));
      filteredData.forEach((product) => {
        const row = worksheet.getRow(rowIndex);
        let formattedStatus = product.productStatus;
        if (product.productStatus === "activate") {
          formattedStatus = t("active_txt");
        } else if (product.productStatus === "deactivate") {
          formattedStatus = t("deactivee_txt");
        }
        row.values = [
          product.priceBand,
          product.productNumber,
          product.description,
          Number(product.price),
          formattedStatus,
        ];
        row.getCell(4).numFmt = "#,##0.00";
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
        rowIndex++;
      });
    } else {
      // Grouped based: current logic
      // Group by priceBand
      const grouped = {};
      filteredData.forEach((item) => {
        if (!grouped[item.priceBand]) {
          grouped[item.priceBand] = [];
        }
        grouped[item.priceBand].push(item);
      });
      // Convert grouped object into sortable array
      const sortedBands = Object.keys(grouped).sort((a, b) => {
        const aHasActive = grouped[a].some((p) => p.productStatus === "Active");
        const bHasActive = grouped[b].some((p) => p.productStatus === "Active");
        if (aHasActive && !bHasActive) return -1;
        if (!aHasActive && bHasActive) return 1;
        return a.localeCompare(b); // alphabetical fallback
      });
      // Report Title
      worksheet.getCell("A1").value = t("price_band_report_tab_txt");
      worksheet.getCell("A1").font = { bold: true, size: 16 };
      // Total Count
      worksheet.getCell("A2").value = `${totalText} ${totalCount}`;
      worksheet.getCell("A2").font = { bold: true };
      // Add some spacing
      rowIndex = 4;
      worksheet.mergeCells("A1:E1");
      worksheet.getCell("A1").alignment = { horizontal: "center" };
      for (const band of sortedBands) {
        const bandProducts = grouped[band];
        // Price Band Title
        const titleRow = worksheet.getRow(rowIndex);
        titleRow.getCell(1).value = `${t("price_band_txt")}: ${band}`;
        titleRow.getCell(1).font = { bold: true, size: 14 };
        titleRow.getCell(1).alignment = { vertical: "middle" };
        // Merge title across 5 columns
        worksheet.mergeCells(`A${rowIndex}:E${rowIndex}`);
        rowIndex++;
        // Header Row
        const headerRow = worksheet.getRow(rowIndex);
        headerRow.values = [
          t("product_code_txt"),
          t("description_txt"),
          t("price(£)_txt"),
          t("product_status_txt"),
        ];
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
        rowIndex++;
        // Data Rows
        bandProducts.forEach((product) => {
          const row = worksheet.getRow(rowIndex);
          let formattedStatus = product.productStatus;
          if (product.productStatus === "activate") {
            formattedStatus = t("active_txt");
          } else if (product.productStatus === "deactivate") {
            formattedStatus = t("deactivee_txt");
          }
          row.values = [
            product.productNumber,
            product.description,
            Number(product.price),
            formattedStatus,
          ];
          // Format Price column
          row.getCell(3).numFmt = "#,##0.00";
          // Add borders to all cells
          row.eachCell((cell) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
          rowIndex++;
        });
        rowIndex++; // spacing between bands
      }
    }
    // Auto column width
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength + 2;
    });
    worksheet.getColumn(1).width = 15;
    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `${t("price_band_report_file_txt")}.xlsx`
    );
  } catch (error) {
    console.error("Excel Export Error:", error);
  }
};
